import { Link } from "react-router-dom";
import { Instagram, Twitter, Linkedin } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import MaklanMonogram from "./MaklanMonogram";

const DIVISIONS_FALLBACK = [
  { name: "Maklan Motors", status: "available" },
  { name: "Maklan Energy", status: "coming_soon" },
  { name: "Maklan Home", status: "coming_soon" },
  { name: "Maklan Tech", status: "coming_soon" },
  { name: "Maklan Machinery", status: "coming_soon" },
];

const COLS = [
  {
    heading: "The Company",
    links: [
      ["/about", "About Maklan"],
      ["/about#standard", "Brand Manifesto"],
      ["/about#roadmap", "Future Roadmap"],
      ["/partner", "Partner With Us"],
      ["/contact", "Contact"],
    ],
  },
  {
    heading: "Motors",
    links: [
      ["/inventory", "The Collection"],
      ["/compare", "Compare"],
      ["/import", "Import on Request"],
      ["/fleet", "Fleet & Corporate"],
      ["/track", "Track a Shipment"],
      ["/warranty", "Warranty"],
      ["/faqs", "Frequently Asked"],
    ],
  },
];

export default function Footer() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/site/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const divs = cfg?.divisions || DIVISIONS_FALLBACK;

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 bg-[#050814] mt-32">
      {/* Editorial band */}
      <div className="border-b border-white/5">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-4">A House of Maklan</div>
            <h3 className="font-display text-4xl md:text-6xl leading-[1.02]">
              Bringing the <em className="italic text-[#C5A059]">world</em> to you.
            </h3>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <p className="text-white/60 leading-relaxed max-w-md lg:ml-auto">
              Maklan is a premium global sourcing and commerce company.
              Motors is our first division.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Identity block */}
          <div className="lg:col-span-4">
            <MaklanMonogram size={36} tone="gold" />
            <div
              className="mt-6 font-display uppercase text-white text-3xl leading-none"
              style={{ letterSpacing: "0.28em" }}
            >
              Maklan
            </div>
            <div className="text-[10px] tracking-[0.42em] uppercase text-white/45 mt-2">
              Motors — The Automotive Division
            </div>
            <p className="mt-8 text-white/55 max-w-sm leading-relaxed">
              A quiet company, building an enduring house of commerce
              between the world's finest makers and the African continent.
            </p>

            <div className="mt-8 flex gap-4 text-white/50">
              <a href="#" className="hover:text-[#C5A059] transition" aria-label="Instagram"><Instagram size={18}/></a>
              <a href="#" className="hover:text-[#C5A059] transition" aria-label="Twitter"><Twitter size={18}/></a>
              <a href="#" className="hover:text-[#C5A059] transition" aria-label="LinkedIn"><Linkedin size={18}/></a>
            </div>
          </div>

          {/* Column: Company */}
          <div className="lg:col-span-2">
            <div className="overline mb-4">{COLS[0].heading}</div>
            <ul className="space-y-3">
              {COLS[0].links.map(([to, label]) => (
                <li key={to}><Link to={to} className="link-under text-white/75 hover:text-white text-sm">{label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Column: Motors */}
          <div className="lg:col-span-2">
            <div className="overline mb-4">{COLS[1].heading}</div>
            <ul className="space-y-3">
              {COLS[1].links.map(([to, label]) => (
                <li key={to}><Link to={to} className="link-under text-white/75 hover:text-white text-sm">{label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Ecosystem */}
          <div className="lg:col-span-4">
            <div className="overline mb-4">The Maklan House</div>
            <ul className="space-y-3">
              {divs.map((d) => {
                const available = d.status === "available";
                return (
                  <li key={d.name} className="flex items-center justify-between border-b border-white/5 pb-3">
                    <span className={`text-sm ${available ? "text-[#C5A059]" : "text-white/80"}`}>{d.name}</span>
                    <span className={`text-[10px] tracking-[0.24em] uppercase ${available ? "text-[#C5A059]" : "text-[#C5A059]/70"}`}>
                      {available ? "Available" : "Coming Soon"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Presence */}
        <div className="mt-16 pt-10 border-t border-white/5 grid md:grid-cols-3 gap-8">
          <div>
            <div className="overline mb-3">Global Presence</div>
            <div className="text-white/70 text-sm leading-relaxed">
              Accra · Lagos · Nairobi · Johannesburg · Casablanca · Dubai ·<br />
              Rotterdam · Hamburg · Shanghai · Yokohama
            </div>
          </div>
          <div>
            <div className="overline mb-3">Written Enquiries</div>
            <div className="text-white/70 text-sm space-y-1">
              <div>{cfg?.email || "info@maklanmotors.com"}</div>
              <div>{cfg?.address || "Accra, Ghana"}</div>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Direct</div>
            <div className="text-white/70 text-sm space-y-1">
              <div>{cfg?.whatsapp || "+233 55 362 3406"}</div>
              <div>Concierge by appointment</div>
            </div>
          </div>
        </div>

        {/* Legal */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Maklan. All rights reserved. Motors, Energy, Home, Tech and Machinery are divisions of Maklan.</div>
          <div className="flex gap-6">
            <Link to="/admin" className="link-under hover:text-white" data-testid="footer-admin-link">Admin</Link>
            <a href="#" className="link-under hover:text-white">Privacy</a>
            <a href="#" className="link-under hover:text-white">Terms</a>
            <a href="#" className="link-under hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-0 h-px divider-line" />
    </footer>
  );
}
